import type {
    ICampaignReportContentItem,
    ICampaignReportContentResponse,
    ICampaignReportCreatorItem,
    ICampaignReportCreatorsResponse,
    ICampaignReportSummary,
    ICampaignReportSyncStatusResponse,
} from '../types/global/campaignReport.types';

/** Unwrap `{ status, data }` campaign API envelopes. */
export function unwrapCampaignReportPayload<T>(response: unknown): T {
    if (response == null) throw new Error('Empty API response');
    const r = response as Record<string, unknown>;
    if (r.status === 'success' && r.data !== undefined) return r.data as T;
    const nested = r.data as Record<string, unknown> | undefined;
    if (nested?.status === 'success' && nested.data !== undefined) return nested.data as T;
    if (r.data !== undefined) return r.data as T;
    return response as T;
}

export function buildReportQuery(params: Record<string, string | number | undefined>): string {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') search.set(key, String(value));
    });
    const qs = search.toString();
    return qs ? `?${qs}` : '';
}

function asRecord(value: unknown): Record<string, unknown> | null {
    return value != null && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : null;
}

function normalizePagination(raw: unknown, itemCount: number): ICampaignReportContentResponse['pagination'] {
    const p = asRecord(raw);
    const page = Number(p?.page ?? 1) || 1;
    const limit = Number(p?.limit ?? itemCount) || Math.max(itemCount, 1);
    const total = Number(p?.total ?? itemCount) || itemCount;
    const hasMore = typeof p?.has_more === 'boolean' ? p.has_more : page * limit < total;
    return { page, limit, total, has_more: hasMore };
}

export function normalizeCampaignReportSummary(raw: unknown): ICampaignReportSummary {
    return unwrapCampaignReportPayload<ICampaignReportSummary>(raw);
}

export function normalizeCampaignReportContent(raw: unknown): ICampaignReportContentResponse {
    const data = unwrapCampaignReportPayload<Record<string, unknown> | ICampaignReportContentItem[]>(raw);
    if (Array.isArray(data)) {
        return { items: data, pagination: normalizePagination(undefined, data.length) };
    }
    const items = (data.items ?? data.content ?? data.rows ?? []) as ICampaignReportContentItem[];
    return {
        items: Array.isArray(items) ? items : [],
        pagination: normalizePagination(data.pagination, Array.isArray(items) ? items.length : 0),
    };
}

export function normalizeCampaignReportCreators(raw: unknown): ICampaignReportCreatorsResponse {
    const data = unwrapCampaignReportPayload<Record<string, unknown> | ICampaignReportCreatorItem[]>(raw);
    if (Array.isArray(data)) {
        return {
            creators: data,
            pagination: normalizePagination(undefined, data.length),
        };
    }
    const creators = (data.creators ?? data.items ?? []) as ICampaignReportCreatorItem[];
    const list = Array.isArray(creators) ? creators : [];
    return {
        creators: list,
        pagination: data.pagination ? normalizePagination(data.pagination, list.length) : undefined,
    };
}

export function normalizeCampaignReportSyncStatus(raw: unknown): ICampaignReportSyncStatusResponse {
    return unwrapCampaignReportPayload<ICampaignReportSyncStatusResponse>(raw);
}
